/**
 * Geração da tabela técnica completa de apreciação de riscos
 * Função para gerar tabela técnica consolidada com todos os dados do risco
 */

import { Risco } from '../core/latex-types';
import { escapeLatex, escapeLatexPreservandoComandos } from '../core/latex-utils';
import { toAbsoluteFotoUrl } from '@/utils/foto-url';
import {
  formatarDescricaoPOTabela,
  formatarDescricaoFETabela,
  formatarDescricaoGSSimplificada,
  formatarDescricaoNPTabela,
} from '../formatters/latex-formatters';
import { formatarNumeroBR, parseNumero } from '../calculators/latex-calculators';
import { processarItensComSubitens } from '../processors/latex-item-processor';
import { isImageValid } from '../visual/latex-visual-elements';

/**
 * Gera uma única imagem centralizada da parte
 * @param imagensParte Array de imagens da parte (fotoAtualUrl1-4)
 * @returns LaTeX formatado com imagens horizontalmente
 */
function gerarImagensTabela(imagensParte: (string | undefined)[]): string {
  const imagensValidas = imagensParte.filter(img => img && isImageValid(img));

  if (imagensValidas.length === 0) {
    return '';
  }

  const resolverImagem = (img: string) => img.startsWith('data:image/') ? img : toAbsoluteFotoUrl(img);

  // Gerar imagens horizontalmente
  if (imagensValidas.length === 1) {
    return `\\includegraphics[height=4.5cm,keepaspectratio]{${escapeLatex(resolverImagem(imagensValidas[0]!))}}`;
  }

  // Múltiplas imagens em linha horizontal
  const larguraImagem = `${Math.floor(95 / imagensValidas.length) / 100}\\linewidth`;
  return `${imagensValidas.map(img => `\\includegraphics[width=${larguraImagem},height=4.5cm,keepaspectratio]{${escapeLatex(resolverImagem(img!))}}`).join('\\hspace{0.2cm}')}`;
}

/**
 * Determina os valores e descrições para SRP/CS baseado nos dados do risco
 * @param categoriaSeguranca Categoria de segurança
 * @param plr PLr (Performance Level required)
 * @returns Objeto com valores formatados
 */
function determinarValoresSRPCS(categoriaSeguranca?: string, plr?: string): {
  severidade: string;
  descricaoSeveridade: string;
  frequencia: string;
  descricaoFrequencia: string;
  possibilidade: string;
  descricaoPossibilidade: string;
  categoria: string;
  plr: string;
} {
  // Severidade: S1 ou S2
  // Se categoriaSeguranca existe e é "1", usar S1, senão S2 (padrão)
  const isS1 = categoriaSeguranca === '1';
  const severidade = isS1 ? 'S1' : 'S2';
  const descricaoSeveridade = isS1 ? 'Lesão Leve' : 'Lesão Grave';

  // Frequência: F1 ou F2 (padrão F2)
  const frequencia = 'F2';
  const descricaoFrequencia = 'Frequente à contínuo';

  // Possibilidade: P1 ou P2 (padrão P1)
  const possibilidade = 'P1';
  const descricaoPossibilidade = 'Possível sob certas condições';

  // Categoria: valor de categoriaSeguranca ou padrão "3"
  const categoria = categoriaSeguranca || '3';

  // PLr: valor de plr ou padrão "d"
  const plrValor = plr || 'd';

  return {
    severidade,
    descricaoSeveridade,
    frequencia,
    descricaoFrequencia,
    possibilidade,
    descricaoPossibilidade,
    categoria,
    plr: plrValor,
  };
}

/**
 * Gera tabela HRN Atual como standalone
 * @param hrnAtual Valor do HRN atual
 * @param POAtual Probabilidade de Ocorrência atual
 * @param FEAtual Frequência de Exposição atual
 * @param GSAtual Grau de Severidade atual
 * @param NPAtual Nº de Pessoas Expostas atual
 * @param descPOAtual Descrição da PO atual
 * @param descFEAtual Descrição da FE atual
 * @param descGSAtual Descrição da GS atual
 * @param descNPAtual Descrição da NP atual
 * @param classificacaoAtual Classificação do risco atual
 * @param corFundoAtual Cor de fundo da classificação
 * @param corTextoAtual Cor do texto da classificação
 * @param toleranciaAtual Tolerância ao risco atual
 * @returns LaTeX formatado com tabela HRN Atual standalone
 */
function gerarTabelaHRNAtual(
  hrnAtual: number,
  POAtual: number,
  FEAtual: number,
  GSAtual: number,
  NPAtual: number,
  descPOAtual: string,
  descFEAtual: string,
  descGSAtual: string,
  descNPAtual: string,
  classificacaoAtual: string,
  corFundoAtual: string,
  toleranciaAtual: string,
  numeroParte: number = 1,
  subNumero: number = 2
): string {
  const corFundoHTML = corFundoAtual === 'myorange' ? 'FF6600' :
    corFundoAtual === 'yellow' ? 'FFFF00' :
      corFundoAtual === 'mygreen' ? '008000' : 'schmersalBlue';

  // Determinar cor do texto: preto para amarelo, branco para outras cores
  const corTexto = corFundoAtual === 'yellow' ? 'black' : 'white';

  const valorPO = formatarNumeroBR(POAtual, POAtual % 1 === 0 ? 0 : 3);
  const valorFE = formatarNumeroBR(FEAtual, FEAtual % 1 === 0 ? 0 : 1);
  const valorGS = formatarNumeroBR(GSAtual, GSAtual % 1 === 0 ? 0 : 1);
  const valorNP = formatarNumeroBR(NPAtual, NPAtual % 1 === 0 ? 0 : 1);
  const valorHRN = formatarNumeroBR(hrnAtual, 3);
  const textoClassificacao = classificacaoAtual;
  const textoTolerancia = escapeLatex(toleranciaAtual);
  const descPO = escapeLatex(descPOAtual);
  const descFE = escapeLatex(descFEAtual);
  const descGS = escapeLatex(descGSAtual);
  const descNP = escapeLatex(descNPAtual);

  return `
  \\vspace{-37pt}
  \\begin{table}[H]
\\begin{flushleft}
\\noindent
\\renewcommand{\\arraystretch}{1.2}
\\setlength{\\tabcolsep}{4pt}
\\footnotesize

\\begin{tabularx}{\\textwidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue} 
\\multicolumn{3}{|l|}{\\textcolor{white}{D.${numeroParte}.${subNumero} - HRN Atual (condição atual da máquina)}} \\\\ \\hline
Nome do parâmetro & Classificação textual & Valor numérico \\\\ \\hline
Probabilidade de Ocorrência (PO) & ${descPO} & ${valorPO} \\\\ \\hline
Frequência de Exposição (FE) & ${descFE} & ${valorFE} \\\\ \\hline
Grau de Severidade (GS) & ${descGS} & ${valorGS} \\\\ \\hline
Nº de Pessoas Expostas ao Risco (NP) & ${descNP} & ${valorNP} \\\\ \\hline
\\rowcolor[HTML]{${corFundoHTML}}
\\cellcolor{white}\\textcolor{black}{HRN = (PO $\\times$ FE $\\times$ GS $\\times$ NP) = ${valorHRN}} & \\multicolumn{2}{|c|}{\\textcolor{${corTexto}}{Classificação: ${textoClassificacao}.}} \\\\ \\hline
\\rowcolor[HTML]{${corFundoHTML}}
\\cellcolor{white}\\textcolor{black}{Tolerância ao risco} & \\multicolumn{2}{|l|}{\\textcolor{${corTexto}}{${textoTolerancia}.}} \\\\ \\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
`;
}

/**
 * Gera tabela HRN Projetado como standalone
 * @param hrnProjetado Valor do HRN projetado
 * @param POProjetado Probabilidade de Ocorrência projetada
 * @param FEProjetado Frequência de Exposição projetada
 * @param GSProjetado Grau de Severidade projetado
 * @param NPProjetado Nº de Pessoas Expostas projetado
 * @param descPOProjetado Descrição da PO projetada
 * @param descFEProjetado Descrição da FE projetada
 * @param descGSProjetado Descrição da GS projetada
 * @param descNPProjetado Descrição da NP projetada
 * @param classificacaoProjetado Classificação do risco projetado
 * @param corFundoProjetado Cor de fundo da classificação
 * @param corTextoProjetado Cor do texto da classificação
 * @param toleranciaProjetado Tolerância ao risco projetado
 * @returns LaTeX formatado com tabela HRN Projetado standalone
 */
function gerarTabelaHRNProjetado(
  hrnProjetado: number,
  POProjetado: number,
  FEProjetado: number,
  GSProjetado: number,
  NPProjetado: number,
  descPOProjetado: string,
  descFEProjetado: string,
  descGSProjetado: string,
  descNPProjetado: string,
  classificacaoProjetado: string,
  corFundoProjetado: string,
  toleranciaProjetado: string,
  numeroParte: number = 1,
  subNumero: number = 5
): string {
  const corFundoHTML = corFundoProjetado === 'myorange' ? 'FF6600' :
    corFundoProjetado === 'yellow' ? 'FFFF00' :
      corFundoProjetado === 'mygreen' ? '008000' : 'schmersalBlue';

  // Determinar cor do texto: preto para amarelo, branco para outras cores
  const corTexto = corFundoProjetado === 'yellow' ? 'black' : 'white';

  const valorPO = formatarNumeroBR(POProjetado, POProjetado % 1 === 0 ? 0 : 3);
  const valorFE = formatarNumeroBR(FEProjetado, FEProjetado % 1 === 0 ? 0 : 1);
  const valorGS = formatarNumeroBR(GSProjetado, GSProjetado % 1 === 0 ? 0 : 1);
  const valorNP = formatarNumeroBR(NPProjetado, NPProjetado % 1 === 0 ? 0 : 1);
  const valorHRN = formatarNumeroBR(hrnProjetado, 3);
  const textoClassificacao = classificacaoProjetado;
  const textoTolerancia = escapeLatex(toleranciaProjetado);
  const descPO = escapeLatex(descPOProjetado);
  const descFE = escapeLatex(descFEProjetado);
  const descGS = escapeLatex(descGSProjetado);
  const descNP = escapeLatex(descNPProjetado);

  return `
    \\vspace{-37pt}
  \\begin{table}[H]
\\begin{flushleft}
\\noindent
\\renewcommand{\\arraystretch}{1.2}
\\setlength{\\tabcolsep}{4pt}
\\footnotesize

\\begin{tabularx}{\\textwidth}{|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}X|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue} 
\\multicolumn{3}{|l|}{\\textcolor{white}{D.${numeroParte}.${subNumero} - HRN Projetado (após medidas de redução de risco)}} \\\\ \\hline
Nome do parâmetro & Classificação textual & Valor numérico \\\\ \\hline
Probabilidade de Ocorrência (PO) & ${descPO} & ${valorPO} \\\\ \\hline
Frequência de Exposição (FE) & ${descFE} & ${valorFE} \\\\ \\hline
Grau de Severidade (GS) & ${descGS} & ${valorGS} \\\\ \\hline
Nº de Pessoas Expostas ao Risco (NP) & ${descNP} & ${valorNP} \\\\ \\hline
\\rowcolor[HTML]{${corFundoHTML}}
\\cellcolor{white}\\textcolor{black}{HRN = (PO $\\times$ FE $\\times$ GS $\\times$ NP) = ${valorHRN}} & \\multicolumn{2}{|c|}{\\textcolor{${corTexto}}{Classificação: ${textoClassificacao}.}} \\\\ \\hline
\\rowcolor[HTML]{${corFundoHTML}}
\\cellcolor{white}\\textcolor{black}{Tolerância ao risco} & \\multicolumn{2}{|l|}{\\textcolor{${corTexto}}{${textoTolerancia}.}} \\\\ \\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
`;
}

/**
 * Gera tabela SRP/CS formatada com 4 colunas e células mescladas
 * @param categoriaSeguranca Categoria de segurança
 * @param plr PLr (Performance Level required)
 * @param sil SIL (Safety Integrity Level)
 * @returns LaTeX formatado com tabela SRP/CS standalone
 */
function gerarTabelaSRPCS(categoriaSeguranca?: string, plr?: string, sil?: string, numeroParte: number = 1, subNumero: number = 4): string {
  const dependeSRPCS = (categoriaSeguranca || plr || sil) ? 'Sim' : 'Não';
  const valores = determinarValoresSRPCS(categoriaSeguranca, plr);

  const txtDependeSRPCS = escapeLatex(dependeSRPCS);
  const txtDescricaoSeveridade = escapeLatex(valores.descricaoSeveridade);
  const txtSeveridade = escapeLatex(valores.severidade);
  const txtCategoria = escapeLatex(valores.categoria);
  const txtDescricaoFrequencia = escapeLatex(valores.descricaoFrequencia);
  const txtFrequencia = escapeLatex(valores.frequencia);
  const txtPlr = escapeLatex(valores.plr);
  const txtDescricaoPossibilidade = escapeLatex(valores.descricaoPossibilidade);
  const txtPossibilidade = escapeLatex(valores.possibilidade);
  return `
\\vspace{-37pt}
\\begin{table}[H]
\\begin{flushleft}
\\noindent
\\renewcommand{\\arraystretch}{1.2}
\\setlength{\\tabcolsep}{4pt}
\\footnotesize
\\begin{tabularx}{\\textwidth}{|X|c|c|c|}
\\hline
\\rowcolor{schmersalBlue} 
\\multicolumn{4}{|l|}{\\textcolor{white}{D.${numeroParte}.${subNumero} - SRP/CS: Sistema de Comando Relacionado à Segurança}} \\\\ \\hline
Depende de uma SRP/CS? & \\multicolumn{2}{c|}{${txtDependeSRPCS}} &  \\\\ \\hline
\\multicolumn{2}{|l|}{Severidade da Lesão} & \\multirow{3}{*}{\\begin{tabular}[c]{@{}c@{}}ABNT NBR\\\\ 14153\\end{tabular}} & Cat. \\\\ \\cline{1-2} \\cline{4-4} 
${txtDescricaoSeveridade} & ${txtSeveridade} &  & \\multirow{2}{*}{${txtCategoria}} \\\\ \\cline{1-2}
\\multicolumn{2}{|l|}{Frequência / Duração da Exposição} &  &  \\\\ \\hline
${txtDescricaoFrequencia} & ${txtFrequencia} & \\multirow{3}{*}{\\begin{tabular}[c]{@{}c@{}}ABNT NBR\\\\ ISO 13849-1\\end{tabular}} & PLr \\\\ \\cline{1-2} \\cline{4-4} 
\\multicolumn{2}{|l|}{Possibilidade de Evitar} &  & \\multirow{2}{*}{${txtPlr}} \\\\ \\cline{1-2}
${txtDescricaoPossibilidade} & ${txtPossibilidade} &  &  \\\\ \\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
`;
}

/**
 * Gera tabela de Recomendações para redução do risco
 * @param medidasControle Array de medidas de controle da parte
 * @param exigenciasNormativas Array de exigências normativas
 * @param adequacoesAdministrativas Array de adequações administrativas
 * @param numeroParte Número da parte para numeração D.X.3
 * @returns LaTeX formatado com tabela de recomendações
 */
function gerarTabelaRecomendacoes(
  medidasControle: string[],
  exigenciasNormativas: string[],
  adequacoesAdministrativas: string[],
  numeroParte: number = 1,
  subNumero: number = 3
): string {
  // Função auxiliar para formatar itens de uma seção
  const formatarItens = (items: string[]): string => {
    const processedItems = processarItensComSubitens(items);
    if (processedItems.length === 0) {
      return '';
    }
    return processedItems.map(item => {
      const texto = item.replace(/^\\item\s+/, '');
      return `\\item ${escapeLatexPreservandoComandos(texto)}`;
    }).join('\n        ');
  };

  // Formatar cada seção
  const medidasLatex = formatarItens(medidasControle);
  const exigenciasLatex = formatarItens(exigenciasNormativas);
  const adequacoesLatex = formatarItens(adequacoesAdministrativas);

  // Verificar se todas as seções estão vazias
  const todasVazias = !medidasLatex && !exigenciasLatex && !adequacoesLatex;

  // Construir conteúdo condicionalmente
  let conteudo = '';

  if (todasVazias) {
    conteudo = 'Não existem medidas adicionais para redução do risco.';
  } else {
    const secoes: string[] = [];

    if (medidasLatex) {
      secoes.push(`Medidas de Controle:
\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]
        ${medidasLatex}
\\end{itemize}`);
    }

    if (exigenciasLatex) {
      secoes.push(`Exigências Normativas:
\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]
        ${exigenciasLatex}
\\end{itemize}`);
    }

    if (adequacoesLatex) {
      secoes.push(`Adequações Administrativas:
\\begin{itemize}[leftmargin=*,itemsep=0pt,parsep=0pt,topsep=2pt]
        ${adequacoesLatex}
\\end{itemize}`);
    }

    conteudo = secoes.join('\n\n\\vspace{4pt}\n');
  }

  return `
  \\vspace{-37pt}
\\begin{table}[H]
\\begin{flushleft}
\\noindent
\\renewcommand{\\arraystretch}{1.2}
\\setlength{\\tabcolsep}{4pt}
\\footnotesize

\\begin{tabularx}{\\textwidth}{|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\textcolor{white}{D.${numeroParte}.${subNumero} - Recomendações para redução do risco}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{
${conteudo}
} \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}
`;
}

/**
 * Gera tabela técnica completa de apreciação de riscos
 * @param parte Nome da parte da máquina
 * @param risco Objeto Risco com todos os dados
 * @param numeroParte Número da parte (para numeração D.x.1, D.x.2, etc.)
 * @param referencia Referência para numeração (No Emitente ou SISMANA)
 * @returns LaTeX formatado com tabela técnica completa
 */
export function gerarTabelaTecnicaApreciacaoRiscos(risco: Risco, numeroParte: number = 1, referencia: string = ''): string {
  // Extrair imagens da parte
  const fotosParte = [
    risco.fotoAtualUrl1,
    risco.fotoAtualUrl2,
    risco.fotoAtualUrl3,
    risco.fotoAtualUrl4
  ];

  // Gerar imagens horizontalmente
  const imagensLatex = gerarImagensTabela(fotosParte);

  // Extrair dados do risco
  const ciclosVida = risco.cicloVida ? risco.cicloVida.split(',').map(c => c.trim()).filter(c => c) : [];
  // Se houver múltiplos ciclos, mostrar todos separados por vírgula; se não houver, usar "Operação Normal" como padrão
  const cicloVidaTexto = ciclosVida.length > 0 ? ciclosVida.join(', ') : 'Operação Normal';
  const eventoPerigoso = risco.eventoPerigoso || 'N/I';
  // Situação encontrada deve ser a descrição da condição atual
  // Priorizar descricaoCondicaoAtual, mas usar adequacoesTecnicas como fallback (compatibilidade)
  const descricaoCondicaoAtual = risco.descricaoCondicaoAtual
    ? risco.descricaoCondicaoAtual.split('\n').filter(l => l.trim())
    : (risco.adequacoesTecnicas ? risco.adequacoesTecnicas.split('\n').filter(l => l.trim()) : []);
  const situacaoEncontrada = descricaoCondicaoAtual.length > 0
    ? descricaoCondicaoAtual.join(' ')
    : 'N/I';

  const tipoPerigo = risco.tipoPerigo || 'Perigo Mecânico';
  const perigosEncontradosFinal = risco.perigosEncontrados
    ? risco.perigosEncontrados.split(',').map(p => p.trim()).filter(p => p && p !== 'teste').join(', ')
    : '';
  const perigosEncontrados = perigosEncontradosFinal || 'N/I';

  const consequencias = risco.consequencias || '';
  const potenciaisConsequenciasFinal = consequencias ? consequencias.split(',').map(c => c.trim()).filter(c => c).join(', ') : '';
  const potenciaisConsequencias = potenciaisConsequenciasFinal || 'N/I';

  // Status de conformidade atual
  const conforme = risco.conformeAtual !== false;
  const statusConformidade = conforme ? 'Conforme' : 'Não conforme';

  // Valores HRN Atual
  const hrnAtual = risco.hrnAtual ?? 0;
  const POAtual = parseNumero(risco.probabilidadeAtual, 0) || (hrnAtual > 0 ? Math.max(1, Math.min(10, Math.round(hrnAtual / 10))) : 1.5);
  const FEAtual = parseNumero(risco.frequenciaAtual, 0) || (hrnAtual > 0 ? Math.max(1, Math.min(10, Math.round(hrnAtual / 3))) : 4);
  const GSAtual = parseNumero(risco.gravidadeAtual, 0) || (hrnAtual > 0 ? Math.max(1, Math.min(10, Math.round(hrnAtual / 5))) : 4);
  const NPAtual = parseNumero(risco.exposicaoAtual, 0) || 1;

  const descPOAtual = formatarDescricaoPOTabela(POAtual);
  const descFEAtual = formatarDescricaoFETabela(FEAtual);
  const descGSAtual = formatarDescricaoGSSimplificada(GSAtual);
  const descNPAtual = formatarDescricaoNPTabela(NPAtual);

  // Determinar classificação HRN Atual - Tabela Cliente (Arcelor Mittal)
  let classificacaoAtual = '';
  let corFundoAtual = '';
  let toleranciaAtual = '';
  if (hrnAtual >= 1000) {
    classificacaoAtual = 'Inaceitável';
    corFundoAtual = 'red';
    toleranciaAtual = 'Alta';
  } else if (hrnAtual >= 500) {
    classificacaoAtual = 'Extremo';
    corFundoAtual = 'red';
    toleranciaAtual = 'Alta';
  } else if (hrnAtual >= 100) {
    classificacaoAtual = 'Muito alto';
    corFundoAtual = 'red';
    toleranciaAtual = 'Alta';
  } else if (hrnAtual >= 50) {
    classificacaoAtual = 'Alto';
    corFundoAtual = 'yellow';
    toleranciaAtual = 'Média';
  } else if (hrnAtual >= 10) {
    classificacaoAtual = 'Significante';
    corFundoAtual = 'yellow';
    toleranciaAtual = 'Média';
  } else if (hrnAtual >= 5) {
    classificacaoAtual = 'Baixo';
    corFundoAtual = 'yellow';
    toleranciaAtual = 'Média';
  } else if (hrnAtual >= 1) {
    classificacaoAtual = 'Muito baixo';
    corFundoAtual = 'mygreen';
    toleranciaAtual = 'Baixa';
  } else {
    classificacaoAtual = 'Aceitável';
    corFundoAtual = 'mygreen';
    toleranciaAtual = 'Baixa';
  }



  // Recomendações - medidas de controle da parte
  const medidasControle = risco.medidasControle ? risco.medidasControle.split('\n').filter(l => l.trim()) : [];
  // Exigências normativas
  const exigenciasNormativas = risco.exigenciasNormativas ? risco.exigenciasNormativas.split('\n').filter(l => l.trim()) : [];
  // Adequações administrativas
  const adequacoesAdministrativas = risco.adequacoesNormativas ? risco.adequacoesNormativas.split('\n').filter(l => l.trim()) : [];

  // Valores HRN Projetado
  const POProjetado = parseNumero(risco.probabilidadeFuturo, 0) || 0.033;
  const FEProjetado = parseNumero(risco.frequenciaFuturo, 0) || FEAtual;
  const GSProjetado = parseNumero(risco.gravidadeFuturo, 0) || GSAtual;
  const NPProjetado = parseNumero(risco.exposicaoFuturo, 0) || NPAtual;

  const hrnProjetadoRecalculado = POProjetado * FEProjetado * GSProjetado * NPProjetado;

  const descPOProjetado = formatarDescricaoPOTabela(POProjetado);
  const descFEProjetado = formatarDescricaoFETabela(FEProjetado);
  const descGSProjetado = formatarDescricaoGSSimplificada(GSProjetado);
  const descNPProjetado = formatarDescricaoNPTabela(NPProjetado);

  // Determinar classificação HRN Projetado - Tabela Cliente (Arcelor Mittal)
  let classificacaoProjetado = '';
  let corFundoProjetado = '';
  let toleranciaProjetado = '';
  if (hrnProjetadoRecalculado >= 1000) {
    classificacaoProjetado = 'Inaceitável';
    corFundoProjetado = 'red';
    toleranciaProjetado = 'Alta';
  } else if (hrnProjetadoRecalculado >= 500) {
    classificacaoProjetado = 'Extremo';
    corFundoProjetado = 'red';
    toleranciaProjetado = 'Alta';
  } else if (hrnProjetadoRecalculado >= 100) {
    classificacaoProjetado = 'Muito alto';
    corFundoProjetado = 'red';
    toleranciaProjetado = 'Alta';
  } else if (hrnProjetadoRecalculado >= 50) {
    classificacaoProjetado = 'Alto';
    corFundoProjetado = 'yellow';
    toleranciaProjetado = 'Média';
  } else if (hrnProjetadoRecalculado >= 10) {
    classificacaoProjetado = 'Significante';
    corFundoProjetado = 'yellow';
    toleranciaProjetado = 'Média';
  } else if (hrnProjetadoRecalculado >= 5) {
    classificacaoProjetado = 'Baixo';
    corFundoProjetado = 'yellow';
    toleranciaProjetado = 'Média';
  } else if (hrnProjetadoRecalculado >= 1) {
    classificacaoProjetado = 'Muito baixo';
    corFundoProjetado = 'mygreen';
    toleranciaProjetado = 'Baixa';
  } else {
    classificacaoProjetado = 'Aceitável';
    corFundoProjetado = 'mygreen';
    toleranciaProjetado = 'Baixa';
  }



  // Gerar tabela LaTeX completa com nova estrutura D.X
  // D.X.1 = Número do perigo: REFERENCIA.XX (header da primeira tabela)
  // D.X.2 = HRN Atual (tabela independente)
  // D.X.3 = Recomendações (tabela independente)
  // D.X.4 = SRP/CS (tabela independente)
  // D.X.5 = HRN Projetado (tabela independente)

  // Formatar número do perigo com 2 dígitos (01, 02, etc.)
  const numeroPerigo = String(numeroParte).padStart(2, '0');
  const numeroCompleto = referencia ? `${referencia}-${numeroPerigo}` : numeroPerigo;

  // Montar subseções condicionalmente. HRN e SRP/CS só aparecem quando habilitados
  // para a parte; a numeração D.X.N é sequencial conforme as subseções presentes.
  const exibirHRN = risco.exibirHRN !== false;
  const exibirSRPCS = risco.exibirSRPCS !== false;

  const subTabelas: string[] = [];
  let subNumero = 2; // D.${numeroParte}.1 é a tabela de informações do perigo

  if (exibirHRN) {
    subTabelas.push(gerarTabelaHRNAtual(
      hrnAtual,
      POAtual,
      FEAtual,
      GSAtual,
      NPAtual,
      descPOAtual,
      descFEAtual,
      descGSAtual,
      descNPAtual,
      classificacaoAtual,
      corFundoAtual,
      toleranciaAtual,
      numeroParte,
      subNumero++
    ));
  }

  subTabelas.push(gerarTabelaRecomendacoes(medidasControle, exigenciasNormativas, adequacoesAdministrativas, numeroParte, subNumero++));

  if (exibirSRPCS) {
    subTabelas.push(gerarTabelaSRPCS(risco.categoriaSeguranca, risco.plr, risco.sil, numeroParte, subNumero++));
  }

  if (exibirHRN) {
    subTabelas.push(gerarTabelaHRNProjetado(
      hrnProjetadoRecalculado,
      POProjetado,
      FEProjetado,
      GSProjetado,
      NPProjetado,
      descPOProjetado,
      descFEProjetado,
      descGSProjetado,
      descNPProjetado,
      classificacaoProjetado,
      corFundoProjetado,
      toleranciaProjetado,
      numeroParte,
      subNumero
    ));
  }

  return `
  \\vspace{-20pt}
  \\begin{table}[H]
\\begin{flushleft}
\\noindent
\\renewcommand{\\arraystretch}{1.2}
\\setlength{\\tabcolsep}{4pt}
\\footnotesize

\\begin{tabularx}{\\textwidth}{|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\textcolor{white}{D.${numeroParte}.1 Número do perigo: ${escapeLatex(numeroCompleto)}}} \\\\
\\hline
${imagensLatex ? `\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{${imagensLatex}} \\\\
\\hline` : ''}\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Ciclo de Vida & ${escapeLatex(cicloVidaTexto)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Atividade / Operação analisada & ${escapeLatex(eventoPerigoso)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Situação encontrada & ${escapeLatex(situacaoEncontrada)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Tipo de perigo & ${escapeLatex(tipoPerigo)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Origem & ${escapeLatex(perigosEncontrados)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Potencial consequência & ${escapeLatex(potenciaisConsequencias)}
\\end{tabular}} \\\\
\\hline
\\multicolumn{1}{|>{\\raggedright\\arraybackslash}X|}{\\begin{tabular}{>{\\raggedright\\arraybackslash}p{3.5cm}|>{\\raggedright\\arraybackslash}p{9.5cm}}
Status de conformidade atual & ${escapeLatex(statusConformidade)}
\\end{tabular}} \\\\
\\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}

${subTabelas.join('\n\n')}

`;
}
